import type { RRuleType } from "verdi-common/utils/RRuleLibrary";

import { ANALYTICS_EVENT, type DurationSource, type IrrigationCreationMethod, trackEvent } from "../events";
import { buildDurationBucket, buildRecurrenceProperties, buildZoneProperties } from "./build-irrigation-properties";

/**
 * Shared shaper for irrigation events across both surfaces (on-demand, scheduler): bind the surface
 * context once, then call one method per event
 */

type IrrigationTrackerContext = {
    creationMethod: IrrigationCreationMethod;
    // Only the keys are used (they become the zone_ids list); the watering % values are ignored here.
    zoneWateringPercentages: Record<string, number> | undefined | null;
};

// durationSource / irrigationSetId / rule are optional because each surface sets only its own subset.
type CreateArgs = {
    eventId: string;
    durationMs: number;
    isScheduledStart: boolean;
    durationSource?: DurationSource;
    irrigationSetId?: string;
    rule?: RRuleType | null;
};
type UpdateArgs = { eventId: string; durationMs: number; rule?: RRuleType | null };
type DeleteArgs = { eventId: string; durationMs: number };

export function createIrrigationEventTracker({ creationMethod, zoneWateringPercentages }: IrrigationTrackerContext) {
    const context = { creation_method: creationMethod, ...buildZoneProperties(zoneWateringPercentages) };

    return {
        trackCreate: ({ eventId, durationMs, isScheduledStart, durationSource, irrigationSetId, rule }: CreateArgs) =>
            trackEvent(ANALYTICS_EVENT.IRRIGATION_EVENT_CREATE, {
                ...context,
                event_id: eventId,
                duration_ms: durationMs,
                duration_bucket: buildDurationBucket(durationMs),
                is_scheduled_start: isScheduledStart,
                duration_source: durationSource,
                irrigation_set_id: irrigationSetId,
                ...buildRecurrenceProperties(rule),
            }),

        trackCreateFail: () =>
            trackEvent(ANALYTICS_EVENT.IRRIGATION_EVENT_CREATE_FAIL, { ...context, error_code: "api_failed" }),

        trackUpdate: ({ eventId, durationMs, rule }: UpdateArgs) =>
            trackEvent(ANALYTICS_EVENT.IRRIGATION_EVENT_UPDATE, {
                ...context,
                event_id: eventId,
                duration_ms: durationMs,
                duration_bucket: buildDurationBucket(durationMs),
                ...buildRecurrenceProperties(rule),
            }),

        trackUpdateFail: (eventId: string) =>
            trackEvent(ANALYTICS_EVENT.IRRIGATION_EVENT_UPDATE_FAIL, {
                ...context,
                error_code: "api_failed",
                event_id: eventId,
            }),

        trackDelete: ({ eventId, durationMs }: DeleteArgs) =>
            trackEvent(ANALYTICS_EVENT.IRRIGATION_EVENT_DELETE, {
                ...context,
                event_id: eventId,
                duration_ms: durationMs,
                duration_bucket: buildDurationBucket(durationMs),
            }),
    };
}
